import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import toast from 'react-hot-toast';
import { adminBulkEmailAPI } from '../services/adminApi';
import EmailAttachments from './EmailAttachments';

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;

const Title = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
`;

const Lead = styled.p`
  color: #6b7280;
  font-size: 0.9rem;
  margin: 0 0 1.25rem 0;
  line-height: 1.5;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 1.1rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9375rem;
`;

const BtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-top: 0.5rem;
`;

const Btn = styled.button`
  padding: 0.55rem 1rem;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.875rem;
  cursor: pointer;
  border: ${(p) => (p.$primary ? 'none' : '1px solid #cbd5e1')};
  background: ${(p) => (p.$primary ? '#043663' : '#fff')};
  color: ${(p) => (p.$primary ? '#fff' : '#043663')};
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Hint = styled.div`
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.4;
`;

const StatsBox = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.65rem;
  margin: 1rem 0;
`;

const Stat = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.75rem 0.85rem;
`;

const StatLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
`;

const StatValue = styled.div`
  margin-top: 0.25rem;
  font-size: 1.35rem;
  font-weight: 800;
  color: ${(p) => (p.$alert ? '#d52b1e' : '#043663')};
`;

const ProgressTrack = styled.div`
  height: 10px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
  margin: 0.75rem 0 0.35rem;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${(p) => Math.min(100, p.$pct || 0)}%;
  background: linear-gradient(90deg, #043663, #0a558c);
  transition: width 0.25s ease;
`;

const PreviewBox = styled.div`
  margin-top: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  max-height: 140px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const PreviewList = styled.ul`
  margin: 0;
  padding: 0.55rem 0.75rem 0.55rem 1.35rem;
  font-size: 0.8rem;
  color: #475569;
  line-height: 1.45;
`;

const ErrorBox = styled(PreviewBox)`
  background: #fef2f2;
  border-color: #fecaca;
  max-height: 100px;
`;

const ErrorList = styled.ul`
  margin: 0;
  padding: 0.55rem 0.75rem 0.55rem 1.35rem;
  font-size: 0.8rem;
  color: #b91c1c;
  line-height: 1.45;
`;

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

export default function AdminBulkEmail({ canSend = false }) {
  const [session, setSession] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [sending, setSending] = useState(false);
  const [attachSessionId, setAttachSessionId] = useState(null);
  const [attachFiles, setAttachFiles] = useState([]);

  useEffect(() => {
    if (!jobId) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await adminBulkEmailAPI.getJob(jobId);
        const data = res?.data?.data || res?.data;
        if (!cancelled && data) {
          setJob(data);
          if (data.status === 'done' || data.status === 'error') {
            setSending(false);
          }
        }
      } catch {
        /* ignore transient poll errors */
      }
    };
    tick();
    const id = setInterval(tick, 1200);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [jobId]);

  const downloadTemplate = async () => {
    try {
      // adminApi interceptor returns response.data already (the Blob for this endpoint)
      const res = await adminBulkEmailAPI.downloadTemplate();
      const blob =
        res instanceof Blob
          ? res
          : new Blob([res], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
      if (blob.size < 50) {
        toast.error('Download failed — file looks empty. Try again or contact support.');
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'hps-bulk-email-template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || 'Download failed');
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setJobId(null);
    setJob(null);
    try {
      const res = await adminBulkEmailAPI.upload(file);
      const data = res?.data?.data || res?.data;
      setSession(data);
      toast.success(`${data.validCount} valid emails ready`);
    } catch (err) {
      setSession(null);
      toast.error(err?.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const clearUpload = async () => {
    if (session?.sessionId) {
      try {
        await adminBulkEmailAPI.clearSession(session.sessionId);
      } catch {
        /* ok */
      }
    }
    setSession(null);
    setJobId(null);
    setJob(null);
  };

  const send = async () => {
    if (!canSend) {
      toast.error('You do not have permission to send');
      return;
    }
    if (!session?.sessionId) {
      toast.error('Upload an Excel file first');
      return;
    }
    if (!subject.trim() || !String(htmlBody).replace(/<[^>]*>/g, '').trim()) {
      toast.error('Subject and message body are required');
      return;
    }
    if (
      !window.confirm(
        `Send this email to ${session.validCount} addresses?\n\nThe uploaded list will be cleared after you confirm — you must re-upload for another send.`
      )
    ) {
      return;
    }
    setSending(true);
    try {
      const res = await adminBulkEmailAPI.send({
        sessionId: session.sessionId,
        subject: subject.trim(),
        htmlBody,
        attachmentSessionId: attachSessionId || undefined,
      });
      const data = res?.data?.data || res?.data;
      setSession(null);
      setAttachSessionId(null);
      setAttachFiles([]);
      setJobId(data.jobId);
      setJob({
        status: 'sending',
        total: data.total,
        processed: 0,
        delivered: 0,
        failed: 0,
      });
      toast.success('Sending started');
    } catch (err) {
      setSending(false);
      toast.error(err?.response?.data?.message || err.message || 'Send failed');
    }
  };

  const pct =
    job?.total > 0 ? Math.round(((job.processed || 0) / job.total) * 100) : 0;

  return (
    <Card>
      <Title>Bulk email</Title>
      <Lead>
        Send one composed message to an arbitrary list of addresses. This is{' '}
        <strong>not</strong> tied to phases, teams, or partner subscribers — only the emails in your
        Excel upload for this session. After you send (or clear), the upload is removed; upload again
        for the next blast.
      </Lead>

      <Field>
        <Label>1. Download template</Label>
        <Hint>One column named <code>email</code>. Add as many rows as you need (limit shown after upload).</Hint>
        <BtnRow>
          <Btn type="button" onClick={downloadTemplate}>
            Download Excel template
          </Btn>
        </BtnRow>
      </Field>

      <Field>
        <Label>2. Upload filled file (.xlsx / .csv)</Label>
        <Input
          type="file"
          accept=".xlsx,.xls,.csv"
          disabled={!canSend || uploading || sending}
          onChange={onFile}
        />
        {uploading ? <Hint>Parsing file…</Hint> : null}
      </Field>

      {session ? (
        <div>
          <StatsBox>
            <Stat>
              <StatLabel>Valid</StatLabel>
              <StatValue>{session.validCount}</StatValue>
            </Stat>
            <Stat>
              <StatLabel>Invalid</StatLabel>
              <StatValue $alert={session.invalidCount > 0}>{session.invalidCount}</StatValue>
            </Stat>
            <Stat>
              <StatLabel>Duplicates removed</StatLabel>
              <StatValue>{session.duplicatesRemoved}</StatValue>
            </Stat>
          </StatsBox>
          <Hint>
            File: {session.filename}. Preview
            {session.validCount > (session.preview || []).length
              ? ` (showing ${(session.preview || []).length} of ${session.validCount})`
              : ''}
            — scroll inside the box; all {session.validCount} will still be sent.
          </Hint>
          <PreviewBox>
            <PreviewList>
              {(session.preview || []).map((e) => (
                <li key={e}>{e}</li>
              ))}
            </PreviewList>
          </PreviewBox>
          {session.invalidSample?.length ? (
            <>
              <Hint style={{ marginTop: '0.75rem' }}>Invalid samples (not sent):</Hint>
              <ErrorBox>
                <ErrorList>
                  {session.invalidSample.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ErrorList>
              </ErrorBox>
            </>
          ) : null}
          <BtnRow>
            <Btn type="button" onClick={clearUpload} disabled={sending}>
              Clear upload
            </Btn>
          </BtnRow>
        </div>
      ) : null}

      <Field style={{ marginTop: '1.25rem' }}>
        <Label>3. Subject</Label>
        <Input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Email subject"
          disabled={sending}
        />
      </Field>

      <Field>
        <Label>4. Message</Label>
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #d1d5db' }}>
          <ReactQuill
            theme="snow"
            value={htmlBody}
            onChange={setHtmlBody}
            modules={quillModules}
            readOnly={sending}
          />
        </div>
      </Field>

      <EmailAttachments
        disabled={!canSend || sending}
        sessionId={attachSessionId}
        onSessionChange={setAttachSessionId}
        files={attachFiles}
        onFilesChange={setAttachFiles}
      />

      <BtnRow>
        <Btn
          type="button"
          $primary
          disabled={!canSend || !session?.sessionId || sending}
          onClick={send}
        >
          {sending ? 'Sending…' : 'Send to uploaded list'}
        </Btn>
      </BtnRow>
      {!canSend ? (
        <Hint style={{ marginTop: '0.5rem' }}>You need edit permission on Settings to send.</Hint>
      ) : null}

      {job ? (
        <div style={{ marginTop: '1.5rem' }}>
          <Label>Send progress</Label>
          <ProgressTrack>
            <ProgressFill $pct={pct} />
          </ProgressTrack>
          <Hint>
            Status: <strong>{job.status}</strong> — {job.processed || 0} / {job.total || 0} (
            {pct}%)
          </Hint>
          <StatsBox>
            <Stat>
              <StatLabel>Delivered</StatLabel>
              <StatValue>{job.delivered || 0}</StatValue>
            </Stat>
            <Stat>
              <StatLabel>Failed</StatLabel>
              <StatValue $alert={(job.failed || 0) > 0}>{job.failed || 0}</StatValue>
            </Stat>
          </StatsBox>
          {job.errors?.length ? (
            <ErrorList>
              {job.errors.map((err) => (
                <li key={`${err.to}-${err.message}`}>
                  {err.to}: {err.message}
                </li>
              ))}
            </ErrorList>
          ) : null}
          {job.status === 'done' ? (
            <Hint style={{ marginTop: '0.75rem' }}>
              Done. Upload a new Excel file to send another bulk email.
            </Hint>
          ) : null}
          {job.status === 'error' ? (
            <Hint style={{ color: '#b91c1c', marginTop: '0.75rem' }}>
              {job.errorMessage || 'Send job failed'}
            </Hint>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}
